import type { Columns } from "@intility/gjallarbru";
import { DataTable } from "@intility/gjallarbru";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import type { ClientListItem, PlatformListItem } from "../../api/types";
import { buildPlatformColumns } from "./platformColumns";

// The default for the column slots. A literal `[]` in the parameter list is a
// new array per render, which changes the useMemo inputs and rebuilds the
// columns every render (the Platforms page omits `columnsBefore`).
const NO_COLUMNS: Columns<PlatformListItem> = [];

export function PlatformsTable({
  platforms,
  clientsByPlatform,
  columnsAfter = NO_COLUMNS,
  columnsBefore = NO_COLUMNS,
  enablePagination = true,
  name,
  navigateState,
  rowClassName,
  toolbarRadius = true,
}: {
  platforms: PlatformListItem[];
  // The platform's clients, rendered by the shared Clients column as tags.
  // Every client item carries its `platformId`, so one list serves every row.
  clientsByPlatform: Map<string, ClientListItem[]>;
  // Columns appended after Clients, e.g. Role and Created on the
  // Platforms page.
  columnsAfter?: Columns<PlatformListItem>;
  // Columns prepended before Name, e.g. the My Hub favourite star.
  columnsBefore?: Columns<PlatformListItem>;
  // My Hub turns pagination off: each section shows all of its rows.
  enablePagination?: boolean;
  // Names the table's saved column layout, so each table keeps its own.
  name: string;
  // Router state forwarded on row navigation. My Hub passes FROM_MYHUB so the
  // platform detail breadcrumb leads back there.
  navigateState?: unknown;
  // Class on every row. My Hub recolours the row hover to match its cards.
  rowClassName?: string;
  toolbarRadius?: boolean;
}) {
  const navigate = useNavigate();
  const columns = useMemo(
    () => buildPlatformColumns(clientsByPlatform, columnsBefore, columnsAfter),
    [clientsByPlatform, columnsBefore, columnsAfter],
  );

  return (
    <DataTable
      // Passing onClick gives each row the hover background and pointer
      // cursor, and skips clicks on buttons, links and other interactive
      // elements inside the row.
      bifrostTableRowProps={{
        className: rowClassName,
        onClick: (
          _event: React.MouseEvent | React.KeyboardEvent,
          row: { original: PlatformListItem },
        ) => {
          navigate(`/platforms/${row.original.id}`, { state: navigateState });
        },
      }}
      columns={columns}
      data={platforms}
      name={name}
      tableOptions={{ enablePagination }}
      toolbarRadius={toolbarRadius}
    />
  );
}
